import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useIntl } from "react-intl";
import { Tooltip } from "@learningblocks/interactives/interactive-infographic-components/lib/Tooltip";
import {
  sanitizeUnsafeHTML,
  sanitizeConfigOptions,
} from "@labxchange/util-sanitization";

import { getHotspotIcon } from "../utils/icons";
import {
  getHotspotStyles,
  getStateButtonStates,
  getStateButtonsGridTemplate,
  getStateButtonsLineAxis,
  handleTabKeyNavigation,
  initTooltipDirection,
  shouldAppendHotspotText,
} from "../utils";
import {
  decodeHtmlEntities,
  convertSupSubToReadableText,
  getScreenReaderFormatters,
} from "../../utils/stringUtils";
import type { StateButtonsHotspotData, HotspotViewData } from "../types";

import className from "../../style.module.scss";

export interface StateButtonsHotspotProps {
  id: string | number;
  data: StateButtonsHotspotData;
  /** Sets the hotspot to an explicit 1-based state. */
  changeState: (id: string | number, state: number) => void;
  handleTrigger: (id: string | number) => void;
  setLatestHotspotRef?: (ref: { current: HTMLButtonElement | null }) => void;
  /** Side of one grid cell in px, from which every hotspot circle is sized. */
  cellSize?: number;
  device?: "mob" | "tab" | "dsk" | string;
  locked?: boolean;
  hide?: boolean;
  idle?: boolean;
  showGrid?: boolean;
  currentView?: number;
  hotspotOrder?: Array<string | number>;
  allHotspots?: Record<string, unknown>;
  /** Fallback direction from the parent Hotspot, used only without viewData. */
  tooltipDirection?: string;
  viewData?: HotspotViewData;
}

/**
 * A "Buttons" state-changer: one button per state, laid out inside the
 * hotspot's authored grid rect and joined by a connector line.
 *
 * Semantically this is a radio group (WAI-ARIA APG): the group is a single stop
 * in the sim's Tab order and Arrow keys move between (and select) options. The
 * roving-tabindex element — the selected button, or the first one when the
 * hotspot is still on its default state — carries `hotspotBtn_${id}`, the id
 * every focus helper in ImageDiscoveryCanvas resolves a hotspot by, so this
 * hotspot participates in `hotspotOrder`, Tab hand-off and view-transition
 * focus exactly like a single-button hotspot does.
 */
export function StateButtonsHotspot(props: StateButtonsHotspotProps) {
  const intl = useIntl();
  const { data, id, cellSize, device, locked, hide, idle, currentView } = props;

  const buttonRefs = useRef<Record<number, HTMLButtonElement | null>>({});

  // Escape hides the hover/focus labels while keeping keyboard focus on the
  // button (WCAG 1.4.13 — dismiss without moving focus), mirroring the other
  // hotspot types. Reset on blur or pointer re-entry so labels come back.
  const [labelsSuppressed, setLabelsSuppressed] = useState(false);

  const hotspotProps = data.props;
  const options = Array.isArray(hotspotProps.options)
    ? hotspotProps.options
    : [];
  const currentState = hotspotProps.currentState ?? 1;

  // Deliberately not memoised on `hotspotProps`: ImageDiscoveryCanvas.changeState
  // mutates `props.currentState` in place and only replaces the hotspots map, so
  // the props object keeps its identity across a state change. A useMemo keyed
  // on it would never recompute. These are cheap enough to just derive.
  const buttonStates: number[] = getStateButtonStates(hotspotProps);

  // The hotspot's authored rect is the grid: one cell per button.
  const gridTemplate = getStateButtonsGridTemplate(data.width, data.height);
  const lineAxis = getStateButtonsLineAxis(
    data.width,
    data.height,
    buttonStates.length
  );

  // Cells available along the line's axis. Buttons fill cells in order, so a
  // rect with spare cells leaves the trailing ones empty and the line stops at
  // the last button rather than running into empty space.
  const cellsOnAxis =
    lineAxis === "horizontal" ? (data.width ?? 1) : (data.height ?? 1);

  const totalCells =
    Math.max(1, Number(data.width) || 1) *
    Math.max(1, Number(data.height) || 1);
  const buttonCount = buttonStates.length;

  // Authoring guard: the grid would spill implicit rows past the bottom of the
  // authored rect. Reported from an effect, not render — the canvas re-renders
  // on a 10s tick and on every hotspot change, so warning in render would spam
  // the console (and fire twice under StrictMode).
  useEffect(() => {
    if (buttonCount > totalCells) {
      console.warn(
        `Hotspot ${id}: ${buttonCount} state buttons do not fit the ` +
          `${data.width}x${data.height} cell rect (${totalCells} cells). ` +
          `Enlarge the hotspot so it has at least one cell per button.`
      );
    }
  }, [id, buttonCount, totalCells, data.width, data.height]);

  const toPlainText = useCallback(
    (text: string) =>
      decodeHtmlEntities(
        sanitizeUnsafeHTML(
          intl.formatMessage(
            {
              id: text,
              defaultMessage: convertSupSubToReadableText(text, intl),
            },
            // stringUtils is untyped JS, so TS widens this to `Object`. It
            // returns rich-text tag handlers that yield strings, which is the
            // overload react-intl needs to give us a string back.
            getScreenReaderFormatters(intl) as Record<
              string,
              (chunks: string[]) => string
            >
          ),
          sanitizeConfigOptions.PlainText
        )
      ),
    [intl]
  );

  // The group's accessible name is the hotspot's own tooltip text — the prompt
  // the individual options answer. Each option supplies its own name, and the
  // radio role conveys its position in the set, so neither is repeated here.
  const groupLabel = useMemo(() => {
    const prompt = data.tooltipText
      ? toPlainText(data.tooltipText)
      : intl.formatMessage({
          id: "Select an option",
          defaultMessage: "Select an option",
        });

    const hotspotText = shouldAppendHotspotText(data)
      ? intl.formatMessage({ id: "hotspot", defaultMessage: "hotspot" })
      : "";

    return hotspotText ? `${prompt} ${hotspotText}` : prompt;
  }, [data, intl, toPlainText]);

  const isDisabled =
    (data.triggered && data.postTrigger === "inactive") ||
    (!!locked && !data.triggered);

  const isVisible =
    (currentView !== undefined &&
      data.visibility_dependencies.views.includes(currentView)) ||
    data.visibility_dependencies.views.length === 0;

  // Roving tabindex: the checked option is the group's single tab stop. On the
  // default state nothing is checked, so the first option takes the tab stop
  // (APG: a radio group with no selection makes its first radio tabbable).
  const checkedIndex = buttonStates.indexOf(currentState);
  const tabbableIndex = checkedIndex === -1 ? 0 : checkedIndex;

  const selectState = (state: number) => {
    if (isDisabled || state === currentState) return;

    props.changeState(id, state);
    if (!data.triggered && data.postTrigger !== "no-change") {
      props.handleTrigger(id);
    }
  };

  const moveTo = (index: number) => {
    const wrapped = (index + buttonStates.length) % buttonStates.length;
    const target = buttonRefs.current[buttonStates[wrapped]];
    if (target) {
      target.focus();
    }
    selectState(buttonStates[wrapped]);
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    index: number
  ) => {
    // Tab hand-off stays identical to every other hotspot: once this hotspot is
    // triggered, Tab jumps to the next unvisited hotspot rather than walking
    // the rest of the group.
    if (event.key === "Tab") {
      handleTabKeyNavigation(
        event,
        id,
        data,
        props.hotspotOrder,
        props.allHotspots,
        currentView
      );
      return;
    }

    // Space and Enter are deliberately absent: the buttons are real <button>
    // elements, so the browser turns those into a click and `onClick` below
    // handles them. Duplicating them here would mean two activation paths that
    // have to be kept in step.
    switch (event.key) {
      case "ArrowDown":
      case "ArrowRight":
        event.preventDefault();
        moveTo(index + 1);
        break;
      case "ArrowUp":
      case "ArrowLeft":
        event.preventDefault();
        moveTo(index - 1);
        break;
      case "Home":
        event.preventDefault();
        moveTo(0);
        break;
      case "End":
        event.preventDefault();
        moveTo(buttonStates.length - 1);
        break;
      case "Escape":
        // Dismiss every tooltip without moving focus (WCAG 1.4.13). They are
        // all hover/focus content — including the selected option's, which
        // carries no state the circle does not already show.
        setLabelsSuppressed(true);
        break;
      default:
        break;
    }
  };

  const hotspotStyles = getHotspotStyles(data, cellSize);

  // Same rule StandardHotspot applies: no hover tooltips on touch, none while
  // locked, and none once Escape has dismissed them (WCAG 1.4.13). The selected
  // option gets no special treatment — its tooltip appears on hover/focus like
  // every other one.
  const showTooltips = device !== "mob" && !locked && !labelsSuppressed;

  // The parent Hotspot derives one tooltip direction from the WHOLE rect, which
  // is wrong here: a 1×7 column can span most of the view, so its last button
  // would open its tooltip downwards off the bottom just because the rect as a
  // whole sits high. Each button re-derives the direction from its own cell, so
  // buttons near an edge open away from it independently of their neighbours.
  const columns = Math.max(1, Number(data.width) || 1);
  const directionForButton = (index: number): string => {
    if (!props.viewData) return props.tooltipDirection || "top-right";

    return initTooltipDirection(
      {
        x_offset: (data.x_offset ?? 0) + (index % columns),
        y_offset: (data.y_offset ?? 0) + Math.floor(index / columns),
        width: 1,
        height: 1,
      },
      props.viewData
    );
  };

  return (
    <div
      className={`${className.stateButtonsGroup} ${hide ? className.showHover : ""} ${
        !props.showGrid ? className.showHoverOverride : ""
      }`}
      // Scoping id for tests and tooling. The hotspot's own `hotspotBtn_${id}`
      // lives on whichever radio currently holds the tab stop, so it cannot
      // double as a stable handle on the group.
      id={`hotspotGroup_${id}`}
      role="radiogroup"
      aria-label={groupLabel}
      style={{ ...gridTemplate }}
    >
      {lineAxis ? (
        <span
          aria-hidden="true"
          data-testid="state-buttons-connector"
          className={`${className.stateButtonsLine} ${
            lineAxis === "horizontal"
              ? className.stateButtonsLineHorizontal
              : className.stateButtonsLineVertical
          }`}
          style={
            {
              "--lx-state-button-count": buttonStates.length,
              "--lx-state-button-cells": cellsOnAxis,
              // Track the ring around each circle, which is itself derived from
              // cellSize — so the line stays the same weight as the rings it
              // joins at every canvas size.
              "--lx-state-button-line-width": hotspotStyles.borderWidth,
            } as React.CSSProperties
          }
        />
      ) : null}

      {buttonStates.map((state, index) => {
        const option = options[state - 1];
        const optionName = option?.name ?? String(state);
        const isSelected = state === currentState;
        const isTabbable = index === tabbableIndex;

        return (
          <div className={className.stateButtonSlot} key={state}>
            <button
              type="button"
              role="radio"
              aria-checked={isSelected}
              aria-label={toPlainText(optionName)}
              id={
                isTabbable
                  ? `hotspotBtn_${id}`
                  : `hotspotBtn_${id}_state${state}`
              }
              ref={(element) => {
                buttonRefs.current[state] = element;
              }}
              disabled={isDisabled}
              tabIndex={isTabbable && isVisible && !isDisabled ? 0 : -1}
              className={[
                className.hotspotContainer,
                className.stateButton,
                isSelected ? className.stateButtonSelected : "",
                locked && !data.triggered ? className.locked : "",
                hide ? className.showHover : "",
                !props.showGrid ? className.showHoverOverride : "",
                hide && idle ? className.idle : "",
              ]
                .filter(Boolean)
                .join(" ")}
              // `onClick` rather than `onMouseDown`: it covers pointer and
              // keyboard through one path (the browser synthesises a click from
              // Space/Enter on a <button>), and it lets a pointer user abort by
              // dragging off before releasing — which matters here because a
              // stray selection can start animations and, with the right
              // dependencies, complete and hide the whole group.
              onClick={() => {
                props.setLatestHotspotRef?.({
                  current: buttonRefs.current[state],
                });
                selectState(state);
              }}
              onMouseEnter={() => setLabelsSuppressed(false)}
              onBlur={() => setLabelsSuppressed(false)}
              onKeyDown={(event) => handleKeyDown(event, index)}
            >
              <span
                className={`${className.hotspot} ${idle ? className.idle : ""}`}
                style={hotspotStyles}
              >
                {isSelected
                  ? null
                  : getHotspotIcon(data, locked, false, data.postTrigger)}

                {/* Each button carries the sim's standard hotspot tooltip,
                    showing its own option name. It must sit inside `.hotspot`:
                    that is where the shared stylesheet expects it, and the
                    hover/focus reveal is driven by `.hotspotContainer` — this
                    button — so the behaviour matches every other hotspot with
                    no extra state here. The button's aria-label already carries
                    the same text, and an aria-label suppresses the element's
                    contents, so this is not announced twice. */}
                {showTooltips && (
                  <Tooltip
                    tooltipDirection={directionForButton(index)}
                    text={optionName}
                  />
                )}
              </span>
            </button>
          </div>
        );
      })}
    </div>
  );
}
